import type { ReactElement } from 'react';
import type { ImpactRecord } from '../api';
import { timeAgo } from '../format';
import { withReturnTo } from '../router';
import { Link } from './Link';
import { SectionEmpty } from './SectionEmpty';
import './PrivateImpactList.css';

/**
 * "Resonated with" — the owner-only impact stream (§4.13): anonymous display
 * name, when, the private gratitude note if one was left, and which Request
 * the resonance happened under. Never exposed on any public surface.
 *
 * §0.9 Reciprocity: each row carries the one tap — `Let them know you got
 * it` — that turns into `They received your note.` on the sender's side. No
 * free text, no thread; this is a receipt, not a chat.
 */
export function PrivateImpactList({ impact, returnTo, logEmberId, onAcknowledge }: {
  impact: ImpactRecord[];
  returnTo?: string;
  /** The Ember whose impact this is, for the request links' telemetry. */
  logEmberId?: string;
  /** The receipt tap (§0.9). Absent → rows render without the action. */
  onAcknowledge?: (resonanceId: string) => void;
}): ReactElement {
  if (impact.length === 0) {
    return (
      <SectionEmpty icon="people">No one has read it yet.</SectionEmpty>
    );
  }
  return (
    <ul className="impact-list">
      {impact.map((r, i) => (
        <li key={r.resonance_id || i} className="impact-item">
          <div className="impact-head">
            <span className="impact-name">{r.anonymous_display_name}</span>
            <span className="impact-time">{timeAgo(r.created_at)}</span>
          </div>
          {r.gratitude_text && (
            <p className="impact-gratitude" dir="auto">“{r.gratitude_text}”</p>
          )}
          {r.request?.id && (
            <Link
              to={withReturnTo(`/requests/${r.request.id}`, returnTo)}
              className="impact-request"
              dir="auto"
              data-log="impact_open"
              data-log-ember-id={logEmberId}
            >
              on: {r.request.canonical_text}
            </Link>
          )}
          {onAcknowledge && r.resonance_id && (
            r.acknowledged ? (
              <p className="impact-ack-done">They know you got it.</p>
            ) : (
              <button
                type="button"
                className="ember-text-action impact-ack"
                data-log="impact_ack"
                data-log-ember-id={logEmberId}
                onClick={() => onAcknowledge(r.resonance_id as string)}
              >
                Let them know you got it
              </button>
            )
          )}
        </li>
      ))}
    </ul>
  );
}
